<template>
  <div class="mt-3">
    <div class="d-flex flex-column gap-3">
      <!-- Verification Services Cards -->
      <div
        v-for="(service, index) in verification_services"
        :key="index"
        class="bg-light d-flex flex-column border-0 rounded-lg shadow-sm"
        :class="{ 
          'opacity-5': disabled_service(service) || (is_any_service_loading && !service.loading)
        }"
      >
        <!-- Service Card -->
        <div class="p-3">
          <!-- Top Section: Icon, Name, Description -->
          <div class="d-flex align-items-start gap-3 flex-grow-1">
            <i class="bi bi-file-earmark-text fs-4 flex-shrink-0 mt-1"></i>
            <div class="flex-grow-1">
              <p class="mb-1 text-xs fw-bold">{{ service.name }}</p>
              <small class="text-muted text-xs">{{ service.description }}</small>
            </div>
          </div>

          <!-- Bottom Section: Status and Buttons -->
          <div class="d-flex justify-content-between align-items-center mt-3">
            <div>
              <!-- Loading -->
              <div v-if="service.loading">
                <small class="text-primary text-xs">
                  <i class="fas fa-spinner fa-spin me-1"></i>
                  Verifying...
                </small>
              </div>
              <!-- Status -->
              <div v-else-if="service.status">
                <small :class="`text-xs ${service.status === 'success' ? 'text-success' : 'text-danger'}`">
                  {{ service.status === 'success' ? '✓ Verified' : '✗ Failed' }}
                </small>
              </div>
            </div>

            <div class="d-flex gap-2">
              <!-- View Results Button (only show if verification has run) -->
              <button
                v-if="service.has_run && service.status"
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-sm text-dark"
                @click="toggle_results_view(service)"
              >
                <i class="bi" :class="service.show_results ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                {{ service.show_results ? 'Hide' : 'View' }} Results
              </button>

              <!-- Run/Re-run Button -->
              <button
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-sm text-dark"
                :disabled="disabled_service(service) || service.loading"
                @click="run_verification(service)"
              >
                {{ service.has_run ? 'Re-run' : 'Run Verification' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Results Section (collapsible) -->
        <div 
          v-if="service.show_results && service.has_run"
          class="border-top bg-light p-3"
        >
          <div class="card border-0 rounded-4 shadow-sm bg-white p-3">
            <!-- Loading State -->
            <div v-if="loading_service_results[service.id]" class="text-center py-4">
              <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="text-xs text-muted mt-2">Loading results...</p>
            </div>

            <!-- Results Display -->
            <div v-else-if="service_results[service.id]">
              <AutoVerificationResults
                :application_id="application_id"
                :entity_profile_id="entity_profile_id"
                :selected_service_id="service.id"
                :auto_fetch="false"
                :external_results="service_results[service.id]"
              />
            </div>

            <!-- No Results -->
            <div v-else class="text-center py-4">
              <i class="bi bi-inbox fs-1 text-muted"></i>
              <p class="text-muted mt-2 mb-0 text-xs">
                No results available for this service.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Generate Report Section - Only show if services are assigned and NOT on lead side -->
      <div 
        v-if="verification_services.length > 0 && !is_lead_context"
        class="card card-header-primary mt-3 rounded-lg"
      >
        <div class="card-body">
          <div class="d-flex align-items-end gap-3">
            <div class="flex-grow-1">
              <div class="d-flex align-items-center fw-bold mb-2 text-white text-xs">
                <div class="generate-icon me-2">
                  <i class="bi bi-file-earmark-text fs-6 flex-shrink-0 mt-1"></i>
                </div>
                Generate Verification Report
              </div>

              <p class="text-xxs text-white mb-2">
                A comprehensive report will be generated once all required verifications are complete.
                The report includes detailed findings from KYC and AML checks.
              </p>

              <div class="d-flex flex-wrap gap-2 text-white">
                <div
                  v-for="req in required_services"
                  :key="req.id"
                  class="requirement-item text-xxs"
                >
                  <div
                    class="requirement-check text-xxs"
                    :class="{ completed: is_service_completed(req) }"
                  >
                    <span v-if="is_service_completed(req)">✓</span>
                  </div>
                  {{ req.name }}
                  <span class="opacity-75">
                    {{ is_service_completed(req) ? 'Completed' : 'Required' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-center">
              <button
                v-if="report_generated"
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-sm text-dark"
                data-bs-toggle="modal"
                data-bs-target="#verification_report_modal"
                @click="view_report">View Report
              </button>

              <button
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-sm text-dark"
                :disabled="!can_generate_report || generating_report"
                @click="generate_report"
              >
                <span
                  v-if="generating_report"
                  class="spinner-border spinner-border-sm text-primary"
                ></span>
                <span v-else>
                  {{ report_generated ? 'Regenerate Report' : 'Generate Report' }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div 
        v-else-if="verification_services.length === 0" 
        class="text-center py-5"
      >
        <i class="bi bi-inbox fs-1 text-muted"></i>
        <p class="text-muted mt-3 mb-0">
          No verification services assigned to this project.
        </p>
      </div>
    </div>
  </div>

  <!-- Verification Report Modal -->
  <teleport to="body">
    <div
      class="modal fade"
      id="verification_report_modal"
      tabindex="-1"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content bg-white rounded-4 shadow-lg">

          <div class="modal-header border-bottom-0 cute-header shadow-lg">
            <p class="modal-title text-white text-xxs">Verification Report</p>
            <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <!-- Body -->
          <div class="modal-body p-4">
            <KYCReport
              v-if="selected_report_service_id"
              :entity_profile_id="resolved_entity_profile_id"
              :service_id="selected_report_service_id"
            />
          </div>

          <div class="modal-footer border-top-0 d-flex justify-content-end">
            <button 
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-sm text-dark" 
              @click="download_report"
              :disabled="downloading_report"
            >
              <span v-if="downloading_report" class="spinner-border spinner-border-sm me-2"></span>
              Download Report
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>

</template>

<script>
import axios from "axios";
import { GET_PROJECTS_ASSIGNED_TO_SERVICE, get_headers, GET_VERIFICATION_STATUSES, GENERATE_KYC_AML_REPORT, VIEW_KYC_REPORT } from "@/utils/constants/constants.js";
import { kycVerify, getKycVerificationResults } from "@/services/verification/integrated_verification_apis/kyc_verification";
import { amlVerify, getAMLVerificationResults } from "@/services/verification/integrated_verification_apis/aml_verification.js";
import { identityverificationVerify, getIDVVerificationResults } from "@/services/verification/integrated_verification_apis/idv_verification.js";
import { cipcVerify, getCIPCVerificationResults } from "@/services/verification/integrated_verification_apis/cipc_verification.js";
import { creditreportVerify, getCreditReportVerificationResults } from "@/services/verification/integrated_verification_apis/credit_report_verification.js";
import alert_mixin from '@/utils/mixins/alert_mixin';
import AutoVerificationResults from "../AutoVerificationResults/AutoVerificationResults.vue";
import KYCReport from '../Reports/verification/KYCReport.vue';

export default {
  name: "VerificationServicesModal",
  components: {
    AutoVerificationResults,
    KYCReport
  },
  mixins: [alert_mixin],
  props: {
    isVerifying: {
      type: Boolean,
      default: false
    },
    entityData: {
      type: Object,
      default: () => ({})
    },
    application_id: {
      type: [String, Number],
      default: null
    },
    entity_profile_id: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      verification_services: [],
      loading: false,
      error: null,
      project_id: sessionStorage.getItem('project_id'),
      service_results: {},
      loading_service_results: {},
      generating_report: false,
      downloading_report: false,
      report_generated: false,
      selected_report_service_id: null,
      // Define which services are required for report generation
      required_service_names: ['KYC', 'AML']
    };
  },
  computed: {
    is_verifying() {
      return this.isVerifying;
    },
    has_selected_services() {
      return this.verification_services.some((service) => service.selected && !this.disabled_service(service));
    },
    is_any_service_loading() {
      return this.verification_services.some((service) => service.loading);
    },
    required_services() {
      return this.verification_services.filter(service => 
        this.is_required_service(service)
      );
    },
    can_generate_report() {
      const has_all_required = this.required_service_names.every(required_name => {
      return this.verification_services.some(service => 
        service.name?.toUpperCase().includes(required_name));
      });
      const all_completed = this.required_services.length > 0 && 
        this.required_services.every(service => 
        service.has_run && service.status === 'success'
      );
      return has_all_required && all_completed;
    },

    resolved_entity_profile_id() {
      const route_entity_id = this.$route?.params?.entity_profile_id;
      if (route_entity_id) return route_entity_id;
      if (this.entity_profile_id) return this.entity_profile_id;
      return sessionStorage.getItem('entity_profile_id');
    },

    is_lead_context() {
      return this.getVerificationContext().type === 'lead';
    }
  },
  mounted() {
    this.fetch_verification_services();
  },
  methods: {

    getVerificationContext() {
      const application_form_id = sessionStorage.getItem("application_form_id");
      const lead_form_id = sessionStorage.getItem("lead_form_id");
      const lead_id = sessionStorage.getItem("lead_id") || this.$route.query.lead_id;

      if (lead_form_id || lead_id) {
        return { 
          type: 'lead', 
          lead_form_id: lead_form_id || null,
          lead_id: lead_id || null 
        };
      }
      
      return { 
        type: 'application', 
        application_form_id: application_form_id || null 
      };
    },
    is_required_service(service) {
      return this.required_service_names.some(name => 
        service.name?.toUpperCase().includes(name)
      );
    },
    
    is_service_completed(service) {
      return service.has_run && service.status === 'success';
    },

    normalize_api_status(status) {
      if (!status) return "error";
      const verification_status = status.toString().toLowerCase();

      if (
        ["success", "pass", "passed", "verified", "completed", "ok"].includes(verification_status)
      ) {
        return "success";
      }
      if (["fail", "failed", "error"].includes(verification_status)) {
        return "error";
      }
      return verification_status;
    },

    normalize_backend_status(backend_status) {
      // Map backend status to frontend status
      const status_map = {
        'pass': 'success',
        'fail': 'error',
        'pending': 'pending'
      };
      return status_map[backend_status] || backend_status;
    },

    async fetch_verification_services() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(GET_PROJECTS_ASSIGNED_TO_SERVICE, {
          headers: get_headers(),
          params: { project_id: this.project_id },
        });

        const parsed_response = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

        if (parsed_response.status === 'success' && parsed_response.data) {
          this.verification_services = parsed_response.data.map((service) => ({
            id: service.id,
            name: service.name,
            description: service.description,
            price: service.price,
            required_fields: service.required_fields,
            date_created: service.date_created,
            is_active: service.is_active,
            selected: false,
            loading: false,
            status: null,
            has_run: false,
            show_results: false,
          }));
          
          // Load statuses from backend instead of localStorage
          await this.load_verification_statuses();
        } else {
          this.$emit('error', parsed_response.message || 'Failed to fetch services');
        }
      } catch (error) {
        this.error = 'Failed to load verification services. Please try again.';
        this.$emit('error', this.error);
      } finally {
        this.loading = false;
      }
    },

    async load_verification_statuses() {
      const application_form_id = sessionStorage.getItem("application_form_id");
      const lead_id = sessionStorage.getItem("lead_id") || this.$route.query.lead_id;
      
      // We need either one to load statuses
      if (!application_form_id && !lead_id) return;

      try {
        const params = {};
        if (application_form_id) {
          params.application_form_id = application_form_id;
        } else if (lead_id) {
          params.lead_id = lead_id;
        }

        const response = await axios.get(GET_VERIFICATION_STATUSES, {
          headers: get_headers(),
          params: params
        });

        const parsed_response = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

        if (parsed_response.status === 'success' && parsed_response.data) {
          const statuses = parsed_response.data;
          
          // Update services with backend statuses
          this.verification_services.forEach(service => {
            if (statuses[service.id]) {
              service.status = this.normalize_backend_status(statuses[service.id].status);
              service.has_run = statuses[service.id].has_run;
            }
          });
        }
      } catch (error) {
        console.warn('Failed to load verification statuses:', error);
      }
    },

    disabled_service(service) {
      const disabled_services = [];
      return disabled_services.includes(service.name?.toUpperCase());
    },

    async toggle_results_view(service) {
      service.show_results = !service.show_results;

      if (service.show_results && !this.service_results[service.id]) {
        await this.fetch_service_results(service);
      }
    },

    async fetch_service_results(service) {
      const context = this.getVerificationContext();

      if (!service?.id) {
        return;
      }
      this.loading_service_results[service.id] = true;

      try {
        const api_call = this.get_service_api(service);
        if (!api_call) {
          return;
        }
        const params = {
          service_id: service.id,
        };

        if (context.type === 'application') {
          const entity_id = this.get_entity_id();

          if (entity_id) {
            params.entity_profile_id = entity_id;
          } else {
            this.show_error("Entity profile ID not found");
            return;
          }
        } else if (context.type === 'lead') {

          if (context.lead_id) {
            params.lead_id = context.lead_id;
            // DO NOT send entity_profile_id for leads
          } else {
            this.show_error("Lead ID not found");
            return;
          }
        } else {
          this.show_error("Unable to determine verification context");
          return;
        }
        const result = await api_call(params);

        if (result) {
          const processed_results = this.process_api_results(result, service);
          this.service_results[service.id] = processed_results;
        } 
      } catch (error) {
        this.show_error("Failed to load verification results: " + error.message);
      } finally {
        this.loading_service_results[service.id] = false;
      }
    },

    get_entity_id() {
      return (
        this.entity_profile_id ||
        sessionStorage.getItem("entity_profile_id")
      );
    },

    get_service_api(service) {
      const serviceApiMap = {
        kyc: getKycVerificationResults,
        aml: getAMLVerificationResults,
        id: getIDVVerificationResults,
        cipc: getCIPCVerificationResults,
        credit: getCreditReportVerificationResults,
      };

      const name = service?.name?.toLowerCase() || "";

      return Object.entries(serviceApiMap).find(([key]) =>
        name.includes(key)
      )?.[1];
    },


    process_api_results(api_result, service) {
      if (api_result.results && api_result.results.length > 0) {
        return api_result.results.map((result, index) => ({
          key: `${service.name}-${service.id}-${index}`,
          label: `${service.name} Results`,
          date: new Date().toLocaleDateString(),
          status: result.status || api_result.status || "success",
          details: this.extract_details_from_result(result),
        }));
      }

      const details = this.extract_details_from_result(api_result.raw_data || api_result);
      return [{
        key: `${service.name}-${service.id}-0`,
        label: `${service.name} Results`,
        date: new Date().toLocaleDateString(),
        status: this.normalize_api_status(api_result.status),
        details,
      }];
    },

    extract_details_from_result(result_item) {
      if (result_item.results_data || result_item.field_results) {
        return result_item;
      }

      if (result_item.raw_data && (result_item.raw_data.results_data || result_item.raw_data.data)) {
        return {
          field_results: result_item.raw_data.field_results || {},
          results_data:
            result_item.raw_data.results_data ||
            result_item.raw_data.data ||
            result_item.raw_data.details ||
            {},
        };
      }

      if (result_item.data && (result_item.data.results_data || result_item.data.details)) {
        return {
          field_results: result_item.data.field_results || {},
          results_data:
            result_item.data.results_data ||
            result_item.data.details ||
            result_item.data ||
            {},
        };
      }

      return {
        field_results: result_item.field_results || {},
        results_data:
          result_item.results_data ||
          result_item.details ||
          result_item.data ||
          {},
      };
    },

    async run_verification(service) {
      service.selected = true;
      this.save_service_id_to_session_storage(service.id);
      await this.start_service_verification(service);
      
      // Auto-show results after successful verification
      if (service.status === 'success') {
        service.show_results = true;
        await this.fetch_service_results(service);
      }
    },

    save_service_id_to_session_storage(serviceId) {
      const current_services = this.get_selected_service_ids_from_session_storage();
      if (!current_services.includes(serviceId)) {
        current_services.push(serviceId);
        sessionStorage.setItem('selected_verification_services', JSON.stringify(current_services));
      }
    },

    get_selected_service_ids_from_session_storage() {
      const stored_services = sessionStorage.getItem('selected_verification_services');
      return stored_services ? JSON.parse(stored_services) : [];
    },

    async call_verification_api(service, payload) {
      const name = (service.name || "").toLowerCase();

      if (name.includes("aml")) {
        return amlVerify(payload);
      } else if (name.includes("credit report")) {
        return creditreportVerify(payload);
      } else if (name.includes("cipc")) {
        return cipcVerify(payload);
      } else if (name.includes("id verification") || name.includes("idv")) {
        return identityverificationVerify(payload);
      } else if (name.includes("kyc")) {
        return kycVerify(payload);
      }

      return kycVerify(payload);
    },

    async start_service_verification(service) {
      const application_form_id = sessionStorage.getItem("application_form_id");
      const lead_form_id = sessionStorage.getItem("lead_form_id");
      const lead_id = sessionStorage.getItem("lead_id") || this.$route.query.lead_id;
      const service_id = service.id;

      // Validate we have either application_form_id OR lead_form_id
      if (!application_form_id && !lead_form_id) {
        this.show_error("Either application_form_id or lead_form_id is required for verification");
        this.$nextTick(() => {
          service.selected = false;
        });
        return;
      }

      service.loading = true;
      service.status = null;

      try {
        const payload = {
          service_id,
        };
        if (application_form_id) {
          payload.application_form_id = application_form_id;
        } else if (lead_form_id) {
          payload.lead_form_id = lead_form_id;
          if (lead_id) {
            payload.lead_id = lead_id;
          }
        }

        sessionStorage.setItem("current_service_id", service_id.toString());

        const result = await this.call_verification_api(service, payload);
        const normalized = this.normalize_api_status(result?.status);

        service.loading = false;
        service.status = normalized;
        service.has_run = true;

        this.save_verification_result_to_session_storage(
          service,
          result,
          normalized
        );

        this.$emit("service-verified", {
          service,
          result,
          context_type: application_form_id ? 'application' : 'lead'
        });
      } catch (error) {
        service.loading = false;
        service.status = 'error';
        service.has_run = true;
        
        this.$emit("service-error", {
          service,
          error,
        });
        const error_message = error?.response?.data?.detail || error?.response?.data?.message || error.message;
        const clean_message = this.format_error_message(error_message);
        this.show_error(clean_message);
      }
    },

    save_verification_result_to_session_storage(service, result, status) {
      const verification_results = JSON.parse(sessionStorage.getItem('verification_results') || '{}');
      verification_results[service.id] = {
        service_id: service.id,
        service_name: service.name,
        result,
        timestamp: new Date().toISOString(),
        status,
      };
      sessionStorage.setItem('verification_results', JSON.stringify(verification_results));
    },

    async generate_report() {
      if (!this.can_generate_report) return;

      this.generating_report = true;

      try {
        const entity_profile_id = this.entity_profile_id || sessionStorage.getItem("entity_profile_id");
        
        const response = await axios.post(GENERATE_KYC_AML_REPORT, {
          entity_profile_id: entity_profile_id,
          verification_services: this.required_services.map(s => s.id)
        }, { headers: get_headers() });

        this.show_success("Report generated successfully!");
        this.report_generated = true;
        
        // Store the first KYC service ID for viewing the report
        const kyc_service = this.required_services.find(s => 
          s.name?.toUpperCase().includes('KYC')
        );
        if (kyc_service) {
          this.selected_report_service_id = kyc_service.id;
        }
        
        this.$emit('report-generated', response.data);
        
      } catch (error) {
        const error_message = error?.response?.data?.detail || error?.response?.data?.message || error.message;
        const clean_message = this.format_error_message(error_message);
        this.show_error(clean_message || "Failed to generate report");
      } finally {
        this.generating_report = false;
      }
    },

    view_report() {
      if (!this.selected_report_service_id) {
        // Find KYC service if not already set
        const kyc_service = this.required_services.find(s => 
          s.name?.toUpperCase().includes('KYC')
        );
        if (kyc_service) {
          this.selected_report_service_id = kyc_service.id;
        } else {
          this.show_error("Unable to view report. KYC service not found.");
          return;
        }
      }
    },

    view_report_for_service(service) {
      this.selected_report_service_id = service.id;
    },

    is_kyc_service(service) {
      return service.name?.toUpperCase().includes('KYC');
    },

    async download_report() {
      if (!this.resolved_entity_profile_id || !this.selected_report_service_id) {
        this.show_error("Unable to download report.");
        return;
      }

      this.downloading_report = true;

      try {
        const params = new URLSearchParams({
          entity_profile_id: this.resolved_entity_profile_id,
          service_id: this.selected_report_service_id,
          download: "true",
        });

        const response = await axios.get(
          `${VIEW_KYC_REPORT}?${params.toString()}`,
          {
            headers: get_headers(),
            responseType: "blob",
          }
        );

        const blob = new Blob([response.data], {
          type: "application/pdf",
        });

        setTimeout(() => {
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.download = `KYC_Verification_Report_${new Date().toISOString().split('T')[0]}.pdf`;

          document.body.appendChild(link);
          link.click();
          link.remove();

          window.URL.revokeObjectURL(url);
          
          this.show_success("Report downloaded successfully!");
        }, 100);

      } catch (error) {
        this.show_error("Failed to download report.");
      } finally {
        this.downloading_report = false;
      }
    },

    reset_selections() {
      this.verification_services.forEach((service) => {
        service.selected = false;
        service.loading = false;
        service.status = null;
        service.has_run = false;
        service.show_results = false;
      });
      sessionStorage.removeItem('selected_verification_services');
      sessionStorage.removeItem('current_service_id');
      this.service_results = {};
      this.report_generated = false;
      this.selected_report_service_id = null;
    },

    format_error_message(message) {
      if (!message) return message;

      message = message.replace(/[[\]'"]+/g, "");
      message = message.replace(/_/g, " ");
      message = message.replace(/\b([a-z])/g, (m) => m.toUpperCase());
      return message;
    }
  },
  emits: ['start-verification', 'error', 'service-verified', 'service-error', 'report-generated']
};
</script>

<style scoped>
.generate-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 0.375rem;
}

.requirement-check {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(76, 175, 80, 0.3);
  border: 2px solid rgba(76, 175, 80, 0.6);
}

.requirement-check.completed {
  background: #4caf50;
  border-color: #4caf50;
}
</style>